<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-gray-800">DRG Report</h2>
    </div>

    <div v-if="summaryStats" class="mb-4">
      <div style="display:flex; gap:12px; margin-bottom:12px; flex-wrap:wrap;">
        <div class="drg-hero-card">
          <div class="drg-hero-value">{{ summaryStats.totalAdmits.toLocaleString() }}</div>
          <div class="drg-hero-label">Admissions</div>
        </div>
        <div class="drg-hero-card">
          <div class="drg-hero-value">{{ summaryStats.distinctPatients.toLocaleString() }}</div>
          <div class="drg-hero-label">Patients</div>
          <div class="drg-hero-sub">{{ summaryStats.avgAdmits }} avg / patient</div>
        </div>
        <div class="drg-hero-card">
          <div class="drg-hero-value" :style="{ color: summaryStats.readmitRate >= 20 ? '#dc2626' : summaryStats.readmitRate >= 10 ? '#d97706' : '#1e293b' }">
            {{ summaryStats.readmits.toLocaleString() }}
          </div>
          <div class="drg-hero-label">30-Day Readmissions</div>
          <div class="drg-hero-sub">{{ summaryStats.readmitRate }}% of admissions</div>
        </div>
        <div class="drg-hero-card" style="border-color:#fed7aa;">
          <div class="drg-hero-value" style="color:#d97706;">{{ summaryStats.acscCount.toLocaleString() }}</div>
          <div class="drg-hero-label">ACSC (Avoidable)</div>
          <div class="drg-hero-sub">{{ summaryStats.acscRate }}% of admissions</div>
        </div>
        <div class="drg-hero-card">
          <div class="drg-hero-value">${{ Math.round(summaryStats.totalCost).toLocaleString() }}</div>
          <div class="drg-hero-label">Total Cost</div>
          <div class="drg-hero-sub">${{ summaryStats.avgCost.toLocaleString() }} avg / admit</div>
        </div>
      </div>
      <div v-if="summaryStats.acscByCondition && Object.keys(summaryStats.acscByCondition).length" style="display:flex; gap:8px; flex-wrap:wrap;">
        <span v-for="(count, condition) in summaryStats.acscByCondition" :key="condition"
          style="font-size:11px; padding:3px 10px; border-radius:12px; background:#fff7ed; color:#92400e; border:1px solid #fed7aa;">
          {{ condition }}: <strong>{{ count }}</strong>
        </span>
      </div>
    </div>

    <div v-if="loading" class="py-10 text-center text-sm text-gray-400">Loading…</div>
    <DataTable
      v-else
      :columns="columns"
      :rows="filteredRows"
      row-key="_rowid"
      :row-class="rowClass"
      max-height="600px"
      saved-view-key="ext-drg-report"
      :has-saved="hasSavedView"
      :saved-just-saved="savedViewJustSaved"
      v-model:sort-model="sortModel"
      export-title="DRG Report"
      export-filename="cclf_drg_report"
      empty-text="No DRG data."
      @save-view="saveView"
      @reset-view="resetView"
    >
      <template #toolbar-left>
        <select v-model="careSetting" class="drg-ctrl" @change="loadData">
          <option value="">All Settings</option>
          <option value="SNF">SNF</option>
          <option value="ALF">ALF</option>
        </select>
        <select v-model="facilityFilter" class="drg-ctrl" style="max-width:200px;">
          <option value="">All Facilities</option>
          <option v-for="f in facilityOptions" :key="f" :value="f">{{ f }}</option>
        </select>
        <select v-model="statusFilter" class="drg-ctrl">
          <option value="">All Statuses</option>
          <option v-for="s in statusOptions" :key="s" :value="s">{{ s }}</option>
        </select>
        <label class="text-xs text-gray-500">From</label>
        <input type="date" v-model="clmFromDt" class="drg-ctrl" @change="loadData" />
        <label class="text-xs text-gray-500">To</label>
        <input type="date" v-model="clmThruDt" class="drg-ctrl" @change="loadData" />
      </template>
    </DataTable>
  </div>
</template>

<script>
import { DataTable } from '@/components/External/table'
import savedView from '@/mixins/savedView'

export default {
  name: 'CCLFReport_DRGReport',
  components: { DataTable },
  mixins: [savedView],
  savedView: {
    key: 'ext-drg-report',
    fields: ['sortModel', 'careSetting', 'facilityFilter', 'statusFilter'],
    scopeProp: 'entityId',
  },
  props: {
    contractId: { type: String, default: null },
    performanceYear: { type: Number, default: null },
    dosthru: { type: String, default: null },
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      careSetting: '',
      facilityFilter: '',
      statusFilter: '',
      allRowData: [],
      summary: null,
      loading: false,
      sortModel: { key: null, dir: 'asc' },
      clmFromDt: this.performanceYear ? `${this.performanceYear}-01-01` : null,
      clmThruDt: new Date().toISOString().slice(0, 10),
      columns: [
        { field: 'mbi', header: 'MBI', type: 'text', sticky: true, width: 130, filterable: true, cellClass: () => 'sensitive' },
        { field: 'patient_name', header: 'Patient', type: 'text', width: 160, filterable: true, cellClass: () => 'sensitive' },
        { field: 'patient_status', header: 'Status', type: 'text', width: 100 },
        { field: 'facility', header: 'Hospital', type: 'text', width: 220, filterable: true, cellClass: () => 'sensitive' },
        { field: 'patient_facility', header: 'Patient Facility', type: 'text', width: 220, filterable: true, cellClass: () => 'sensitive' },
        { field: 'admission_type', header: 'Admission Type', type: 'text', width: 130 },
        { field: 'is_readmit', header: 'Readmit', type: 'text', width: 90,
          format: v => (v === true || v === 'Yes') ? 'Yes' : '',
          cellStyle: v => (v === true || v === 'Yes') ? { color: '#dc2626', fontWeight: '500' } : null },
        { field: 'acsc_condition', header: 'ACSC Condition', type: 'text', width: 150, filterable: true,
          cellStyle: v => v ? { color: '#d97706', fontWeight: '500' } : null },
        { field: 'hcc_code', header: 'HCC', type: 'number', width: 70 },
        { field: 'hcc_description', header: 'HCC Category', type: 'text', width: 280 },
        { field: 'drg', header: 'DRG', type: 'text', width: 75 },
        { field: 'drg_description', header: 'DRG Description', type: 'text', width: 300 },
        { field: 'primary_dx', header: 'Dx Code', type: 'text', width: 90 },
        { field: 'primary_dx_description', header: 'Primary Diagnosis', type: 'text', width: 300 },
        { field: 'admit_date', header: 'Admit', type: 'date', width: 110 },
        { field: 'discharge_date', header: 'Discharge', type: 'date', width: 110 },
        { field: 'los', header: 'LOS', type: 'number', width: 70 },
        { field: 'paid_amount', header: 'Paid Amount', type: 'currency', width: 140,
          format: v => (v == null) ? '—' : (Number(v) <= 0 ? '$0 (Denial)' : '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })) },
        { field: 'patient_admissions', header: '# Admissions', type: 'number', width: 120 },
      ],
    }
  },
  computed: {
    facilityOptions() {
      const s = new Set()
      this.allRowData.forEach(r => { if (r.patient_facility) s.add(r.patient_facility) })
      return [...s].sort()
    },
    statusOptions() {
      const s = new Set()
      this.allRowData.forEach(r => { if (r.patient_status) s.add(r.patient_status) })
      return [...s].sort()
    },
    filteredRows() {
      let rows = this.allRowData
      if (this.facilityFilter) rows = rows.filter(r => r.patient_facility === this.facilityFilter)
      if (this.statusFilter) rows = rows.filter(r => r.patient_status === this.statusFilter)
      // Default grouping: same-patient rows together, admit_date ascending within.
      return [...rows].sort((a, b) => {
        const an = (a.patient_name || '').toLowerCase(); const bn = (b.patient_name || '').toLowerCase()
        if (an !== bn) return an < bn ? -1 : 1
        return (a.admit_date || '') < (b.admit_date || '') ? -1 : 1
      })
    },
    summaryStats() {
      const rows = this.filteredRows
      if (!rows || !rows.length) return null
      const totalAdmits = rows.length
      const patients = new Set(rows.map(r => r.bene_mbi_id || r.patient_name))
      const distinctPatients = patients.size
      const totalCost = rows.reduce((s, r) => s + (parseFloat(r.paid_amount) || 0), 0)
      const readmits = rows.filter(r => r.is_readmit === 'Yes' || r.is_readmit === true).length
      const acscRows = rows.filter(r => r.acsc_condition && r.acsc_condition !== 'None' && r.acsc_condition !== '')
      const acscCount = acscRows.length
      const acscByCondition = {}
      acscRows.forEach(r => { acscByCondition[r.acsc_condition] = (acscByCondition[r.acsc_condition] || 0) + 1 })
      return {
        totalAdmits, distinctPatients, readmits, totalCost,
        avgAdmits: distinctPatients ? (totalAdmits / distinctPatients).toFixed(1) : '0',
        avgCost: totalAdmits ? Math.round(totalCost / totalAdmits) : 0,
        readmitRate: totalAdmits ? parseFloat((readmits / totalAdmits * 100).toFixed(1)) : 0,
        acscCount,
        acscRate: totalAdmits ? parseFloat((acscCount / totalAdmits * 100).toFixed(1)) : 0,
        acscByCondition,
      }
    },
  },
  watch: {
    contractId() { this.loadData() },
    performanceYear() { this.loadData() },
    dosthru() { this.loadData() },
    entityId() { this.loadData() },
  },
  mounted() {
    if (this.contractId && this.performanceYear) this.loadData()
  },
  methods: {
    rowClass(row) {
      return (row.is_readmit === true || row.is_readmit === 'Yes') ? 'drg-readmit-row' : null
    },
    async loadData() {
      if (!this.contractId || !this.performanceYear) return
      this.loading = true
      try {
        const payload = {
          contract_id: this.contractId, performance_year: this.performanceYear,
          dosthru: this.dosthru, entity_id: this.entityId,
          care_setting: this.careSetting || undefined,
        }
        if (this.clmFromDt) payload.clm_from_dt = this.clmFromDt
        if (this.clmThruDt) payload.clm_thru_dt = this.clmThruDt
        const response = await this.$axios.post('/api/portfolio_management/cclf-report/drg-report/', payload)
        // Stable row id for keying (source rows have no unique key).
        this.allRowData = (response.data.rows || []).map((r, i) => ({ ...r, _rowid: i }))
        this.summary = response.data.summary || null
      } catch (error) {
        console.error('Failed to load DRG report:', error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.drg-hero-card { flex: 1; min-width: 140px; padding: 12px 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; }
.drg-hero-value { font-size: 22px; font-weight: 700; color: #1e293b; }
.drg-hero-label { font-size: 12px; color: #64748b; margin-top: 2px; }
.drg-hero-sub { font-size: 11px; color: #94a3b8; margin-top: 1px; }
.drg-ctrl { font-size: 12px; border: 1px solid #E8E3DC; border-radius: 6px; padding: 4px 8px; background: #fff; color: #4A4540; }
:deep(.drg-readmit-row) td { background: #fef2f2 !important; }
</style>
